import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowRight, Lock } from 'lucide-react';
type Preview = {
  src: string;
  label: string;
  caption: string;
};
const PREVIEWS: Preview[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/govoq7pVoPCopDTjjtj6E6/IMG_1873.jpg',
  label: 'Sistema organizado',
  caption: 'Índice del Tomo 1 — 7 secciones'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/x1icAa1n2EA4pWA1LGhqkt/IMG_1874.jpg',
  label: 'Razonamiento clínico',
  caption: 'Pensamiento hipotético-deductivo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/ppHNgcNsadR1qovGm7XfAD/IMG_1871.jpg',
  label: 'Anatomía funcional',
  caption: 'Anatomía funcional de la cadera'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/6SdybWKmrRUboKBUxZRVVg/IMG_1869.jpg',
  label: 'Biomecánica clínica',
  caption: 'Segmentos vertebrales y curvaturas'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fndTKCNKeovaj5ugBYgd6f/IMG_1875.jpg',
  label: 'Movilidad articular',
  caption: 'Rangos normales por articulación'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iKtEG8XHpEQMZ4mKgRqrKj/IMG_1876.jpg',
  label: 'Evaluación funcional',
  caption: 'Escala de Daniels paso a paso'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7TNPgFEAqg9XeAg1erKnxB/IMG_1870.jpg',
  label: 'Red flags clínicas',
  caption: '5D y 3N — alarmas cervicales'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/twFsDqHpBiGjkSA9XaeUMe/IMG_1877.jpg',
  label: 'Diagnóstico diferencial',
  caption: 'Columna lumbar — 4 cuadros'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/7r8XEQJTF7HsSVWZM1o5XR/IMG_1878.jpg',
  label: 'Pruebas ortopédicas',
  caption: 'Muñeca y mano — diferencial'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/3b1RRd7nWcBevXDdB1B4eg/IMG_1868.jpg',
  label: 'Tomos organizados',
  caption: 'Índice del Tomo 3 — Columna'
}];
export function ContentPreview() {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 8);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };
  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateArrows, {
      passive: true
    });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, []);
  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-preview-card]');
    const step = card ? card.offsetWidth + 24 : el.clientWidth * 0.8;
    el.scrollBy({
      left: dir * step,
      behavior: 'smooth'
    });
  };
  return <section id="content-preview" className="relative overflow-hidden bg-white py-10 sm:py-14 md:py-16" data-id="element-47" data-mp="3ukmr5de5kz43">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" data-id="element-48" data-mp="3plilgwqftjii">
        <div className="absolute inset-0" style={{
        backgroundImage: 'linear-gradient(rgba(11,27,51,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(11,27,51,0.35) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} data-id="element-49" data-mp="3knh2ebt0hk71" />
      </div>
      <div aria-hidden className="absolute top-0 left-0 right-0 h-20 bg-mist-100 pointer-events-none" style={{
      maskImage: 'linear-gradient(to bottom, black, transparent)',
      WebkitMaskImage: 'linear-gradient(to bottom, black, transparent)'
    }} data-id="element-50" data-mp="cd5qzjtibaw4" />

      {/* Header */}
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 sm:mb-9" data-id="element-51" data-mp="102hxud1d36v9">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gold/40 bg-gold/10 text-gold-deep text-[9px] sm:text-xs font-bold tracking-[0.14em] uppercase mb-3 sm:mb-4" data-id="element-52" data-mp="kl06q44ic7fi">
          <span className="w-1 h-1 rounded-full bg-gold" data-id="element-53" data-mp="18cfdqa78bcx3"></span>
          <span data-id="element-54" data-mp="18dqf63d7c8l1">Material real del interior</span>
        </motion.div>

        <motion.h2 initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.05
      }} className="font-heading text-navy-950 leading-[1.14] tracking-tight text-[1.35rem] sm:text-[2.1rem] md:text-[2.4rem] mb-2.5 max-w-3xl" data-id="element-55" data-mp="tmge0300ztfi">
          <span className="font-extrabold" data-id="element-56" data-mp="36tzcb093vt1p">
            No tienes que imaginar qué recibirás.
          </span>{' '}
          <span className="font-editorial italic text-gold-deep font-medium" data-id="element-57" data-mp="3livggm81klx1">
            Puedes verlo.
          </span>
        </motion.h2>

        <motion.p initial={{
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="text-[13px] sm:text-[15.5px] text-navy-800/70 leading-relaxed max-w-2xl" data-id="element-58" data-mp="3gkamv5nap0hk">
          Estas son{' '}
          <span className="text-navy-950 font-semibold" data-id="element-59" data-mp="h4dr4hc8g3qa">
            páginas reales incluidas dentro de Atlas Clínico
          </span>
          : razonamiento clínico, anatomía funcional, movilidad articular,
          escalas de evaluación, señales de alerta, diagnóstico diferencial y
          pruebas ortopédicas.
        </motion.p>
      </div>

      {/* Scroller — exploratory horizontal browsing, by design */}
      <div className="relative" data-id="element-60" data-mp="3fux3114npy0r">
        {/* Edge fades */}
        <div className="hidden md:block pointer-events-none absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10" data-id="element-61" data-mp="3ftc7b86lw91b" />
        <div className="hidden md:block pointer-events-none absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10" data-id="element-62" data-mp="2n7kewmiuli5c" />

        {/* Prev / Next buttons (desktop) */}
        <div className="hidden md:flex absolute left-3 lg:left-6 top-1/2 -translate-y-1/2 z-20" data-id="element-63" data-mp="2w6xqbkqj8gsp">
          <button onClick={() => scrollByCard(-1)} disabled={!canPrev} aria-label="Anterior" className={`w-11 h-11 rounded-full bg-navy-900 text-gold hover:bg-gold hover:text-navy-950 transition-colors duration-200 ease-out flex items-center justify-center shadow-[0_8px_24px_rgba(11,27,51,0.25)] ${canPrev ? 'opacity-100' : 'opacity-25 cursor-not-allowed'}`} data-id="element-64" data-mp="30yply9ojpcpp">
            <ChevronLeft size={20} strokeWidth={2.5} data-id="element-65" data-mp="1w0c2y5pxlily" />
          </button>
        </div>
        <div className="hidden md:flex absolute right-3 lg:right-6 top-1/2 -translate-y-1/2 z-20" data-id="element-66" data-mp="1rwwa7kgzg2ej">
          <button onClick={() => scrollByCard(1)} disabled={!canNext} aria-label="Siguiente" className={`w-11 h-11 rounded-full bg-navy-900 text-gold hover:bg-gold hover:text-navy-950 transition-colors duration-200 ease-out flex items-center justify-center shadow-[0_8px_24px_rgba(11,27,51,0.25)] ${canNext ? 'opacity-100' : 'opacity-25 cursor-not-allowed'}`} data-id="element-67" data-mp="1mzdyxnfhtb3d">
            <ChevronRight size={20} strokeWidth={2.5} data-id="element-68" data-mp="149ei1ntt32b1" />
          </button>
        </div>

        <div ref={scrollerRef} className="scrollbar-hide overflow-x-auto scroll-smooth snap-x snap-mandatory" style={{
        scrollPaddingLeft: '1rem',
        scrollPaddingRight: '1rem',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-69" data-mp="z7ythlsf6dnw">
          <div className="flex gap-3 sm:gap-6 px-4 sm:px-6 lg:px-12 pt-2 pb-5 sm:pb-6" data-id="element-70" data-mp="2n4n6o2cr0pjr">
            {PREVIEWS.map((p, idx) => <motion.div key={idx} data-preview-card initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: idx * 0.06,
            duration: 0.5
          }} className="snap-start flex-shrink-0 w-[78vw] max-w-[260px] sm:w-[290px] md:w-[330px] group" data-id="element-71" data-mp="295ne4jm47qib" data-mpi={idx}>
                {/* Page frame */}
                <div className="relative" data-id="element-72" data-mp="1lft56d4x2m3c">
                  {/* Soft glow behind card */}
                  <div className="absolute inset-0 bg-gold/[0.08] blur-2xl rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-73" data-mp="11vc6cw7oxkiq" />

                  <div className="relative rounded-2xl overflow-hidden bg-white ring-1 ring-navy-900/[0.07] group-hover:ring-gold/40 shadow-[0_10px_30px_rgba(11,27,51,0.10)] group-hover:shadow-[0_16px_40px_rgba(11,27,51,0.16)] transition-shadow duration-300 ease-out aspect-[3/4]" data-id="element-74" data-mp="1bt0c0h6u4cfo">
                    <img src={p.src} alt={p.caption} width={680} height={907} loading="lazy" decoding="async" {...{
                  fetchpriority: 'low'
                } as any} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]" data-id="element-75" data-mp="1fulinksrzuhf" />

                    {/* Top gold accent strip */}
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-gold to-transparent opacity-60" data-id="element-76" data-mp="24vi0e6glmu7t" />

                    {/* Page number chip */}
                    <div className="absolute top-3 right-3 bg-navy-950/85 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1 text-[10px] font-bold text-gold tracking-wider" data-id="element-77" data-mp="1v4x2ymcnvvqh">
                      {String(idx + 1).padStart(2, '0')} /{' '}
                      {String(PREVIEWS.length).padStart(2, '0')}
                    </div>
                  </div>
                </div>

                {/* Label below */}
                <div className="mt-2.5 px-0.5" data-id="element-78" data-mp="2dwy826g14i8c">
                  <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold tracking-[0.15em] uppercase text-gold-deep mb-1" data-id="element-79" data-mp="29u1q8hybcq5g">
                    <span className="w-3 h-px bg-gold" data-id="element-80" data-mp="12ddhevsle5lb" />
                    {p.label}
                  </div>
                  <p className="text-[12.5px] sm:text-[14.5px] text-navy-950 font-medium leading-snug" data-id="element-81" data-mp="3a1gl505p6hve">
                    {p.caption}
                  </p>
                </div>
              </motion.div>)}

            {/* Tail CTA card */}
            <motion.a data-preview-card href="#pricing" initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            delay: PREVIEWS.length * 0.06,
            duration: 0.5
          }} className="snap-start flex-shrink-0 w-[78vw] max-w-[260px] sm:w-[290px] md:w-[330px] group" data-id="element-82" data-mp="3j0lgxypvjx3v">
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-gold/40 bg-navy-900 flex flex-col items-center justify-center text-center px-6 group-hover:border-gold transition-[border-color,box-shadow] duration-300 ease-out shadow-[0_12px_34px_rgba(11,27,51,0.18)] group-hover:shadow-[0_18px_44px_rgba(11,27,51,0.26)]" data-id="element-83" data-mp="27gpsnkb8p3o1">
                <div className="absolute inset-0 bg-gold/[0.05] opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out" data-id="element-84" data-mp="3opk0wgzf5dyz" />
                <div className="relative w-12 h-12 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center mb-4" data-id="element-85" data-mp="3jsbiqge9t9xq">
                  <Lock size={20} className="text-gold" strokeWidth={2} data-id="element-86" data-mp="3tomzpjot2nyi" />
                </div>
                <p className="relative text-[11px] tracking-[0.18em] uppercase text-gold/80 font-bold mb-2" data-id="element-87" data-mp="ba5lfrpx3u57">
                  + Mucho más
                </p>
                <h3 className="relative font-heading font-bold text-white text-[17px] sm:text-xl leading-tight mb-2" data-id="element-88" data-mp="79loao5cgqj2">
                  El sistema completo te espera
                </h3>
                <p className="relative text-xs sm:text-sm text-gray-400 leading-relaxed mb-5" data-id="element-89" data-mp="gb1oy46cxea3">
                  6 tomos · 5 herramientas · Casos reales · Algoritmos de
                  decisión
                </p>
                <span className="relative inline-flex items-center gap-2 bg-gold text-navy-950 text-xs sm:text-sm font-bold px-4 py-2 rounded-full transition-transform duration-200 ease-out group-hover:scale-105" data-id="element-90" data-mp="14saiw9nvend9">
                  Ver el acceso completo
                  <ArrowRight size={14} strokeWidth={2.5} data-id="element-91" data-mp="h4is611l27sg" />
                </span>
              </div>
              <div className="mt-2.5 px-0.5" data-id="element-92" data-mp="lvr87fxf4rel">
                <div className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold tracking-[0.15em] uppercase text-gold-deep mb-1" data-id="element-93" data-mp="lxlrkpt1kbgk">
                  <span className="w-3 h-px bg-gold" data-id="element-94" data-mp="c6r3rh05d3t0" />
                  Acceso completo
                </div>
                <p className="text-[12.5px] sm:text-[14.5px] text-navy-950 font-medium leading-snug" data-id="element-95" data-mp="3ucly7ut4ahb9">
                  Y mucho más dentro de Atlas Clínico
                </p>
              </div>
            </motion.a>
          </div>
        </div>

        {/* Mobile scroll hint */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="md:hidden flex items-center justify-center gap-1.5 text-[11px] text-navy-800/60 mt-2 px-4" data-id="element-96" data-mp="3maczbz2sjmfp">
          <span data-id="element-97" data-mp="33ketzqqdo7fr">Desliza para explorar más páginas</span>
          <ChevronRight size={13} className="animate-pulse" data-id="element-98" data-mp="33itx3uglcceq" />
        </motion.div>
      </div>
    </section>;
}